import { Box, Fade, Grid, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import PaymentHeader from '@acorn-portal/shared/src/components/header/PaymentHeader';
import { formatCurrency } from '@acorn-portal/shared/src/utils';
import { Formik, Form } from 'formik';
import { RenewalRatingResponse, SavedPaymentMethodDetails } from '@acorn-portal/shared/src/services/renewalsApi';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import AffordabilityLendingQuestions from '@acorn-portal/shared/src/components/renewal/affordable-lending-questions';
import checkoutSchema from '@acorn-portal/shared/src/utils/validation/CheckoutSchema';
import { Address } from '@acorn-portal/shared/src/services/api';
import DirectDebitDetails from '@acorn-portal/shared/src/components/renewal/direct-debit-details';
import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import { AddCard, CreditCard } from '@mui/icons-material';
import BraintreePaymentForm from '@acorn-portal/shared/src/components/renewal/braintree-checkout';
import useHandleRenewalPayment from '@acorn-portal/shared/src/hooks/useHandleRenewalPayment';
import { ICreateNewRenewalVersionResponse } from '@acorn-portal/shared/src/store/renewal.slice';
import ProblemModal from '@acorn-portal/shared/src/components/modal/ProblemModal';
import { PAYMENT_PROBLEM_MODAL } from '@acorn-portal/shared/src/utils/modalText';
import StripePaymentForm from '@acorn-portal/shared/src/components/renewal/stripe-checkout';
import { PaymentProvider } from '../../services/renewalsApiV2';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

export interface IRenewalMakePaymentForm {
	billingDetails?: 'yes' | 'no';
	cardholderFirstName: string;
	cardholderLastName: string;
	cardholderEmail: string;
	address: Address;
	saveCard: boolean;
	premiumConfirm?: 'yes' | 'no';
	affordable?: 'yes' | 'no';
	account_number?: string;
	sort_code?: string;
	bank_name?: string;
	accept_dd_terms?: boolean;
	new_direct_debit?: boolean;
	is_account_holder?: 'yes' | 'no';
	is_only_signatory_required?: 'yes' | 'no';
	stripe?: boolean;
	id?: string;
}

const getUpFrontSettlement = (rate: RenewalRatingResponse): SavedPaymentMethodDetails | undefined => {
	return rate.balanceSettlementDetails;
};

const MakePayment = () => {
	const renewal = useAppSelector((x: { renewal: ICreateNewRenewalVersionResponse }) => x.renewal);

	const hasExistingCards = false;
	const [useExistingCard, setUseExistingCard] = useState(hasExistingCards);

	const requiresPayment = Boolean(renewal.paymentOptions.method === 'total' || renewal.paymentOptions.deposit);

	const isFinance = renewal.paymentOptions.method === 'finance';
	const validatePremium = import.meta.env.VITE_AUTH_REALM !== 'Motorcade'; // TODO: Refactor this when switching to new pages strategy
	const isStripe = renewal.renewalRatingResponse?.paymentProvider === PaymentProvider.STRIPE;

	const requiredDirectDebitInfo = renewal.renewalRatingResponse
		? getUpFrontSettlement(renewal.renewalRatingResponse)
		: undefined;

	const initialValues: IRenewalMakePaymentForm = {
		premiumConfirm: undefined,
		affordable: undefined,
		billingDetails: undefined,
		cardholderFirstName: '',
		cardholderLastName: '',
		cardholderEmail: '',
		address: {
			line1: '',
			city: '',
			country: 'UK',
			county: '',
			postcode: '',
		},
		saveCard: true,
		account_number: undefined,
		sort_code: undefined,
		accept_dd_terms: false,
		is_only_signatory_required: undefined,
		is_account_holder: undefined,
		id: undefined,
	};

	const newDirectDebitPaymentDay = renewal.paymentOptions.deposit
		? renewal.renewalRatingResponse?.financePlanWithDeposit?.directDebitPaymentSchedule.collectionDay
		: renewal.renewalRatingResponse?.financePlanWithoutDeposit?.directDebitPaymentSchedule.collectionDay;

	const existingCard = (useExistingCard ?? false) && requiresPayment;

	const {
		handleStripePaymentSubmit,
		handleBraintreePaymentSubmit,
		submitting,
		problem,
		setProblem,
		RenewalPaymentError,
	} = useHandleRenewalPayment(requiresPayment, useExistingCard, renewal, requiredDirectDebitInfo);

	return (
		<Paper
			elevation={1}
			sx={{
				borderRadius: '8px',
			}}
		>
			<LoadingModal open={submitting} message='Processing Payment' />
			<Fade in>
				<Box
					sx={{
						pb: 4,
						margin: 2,
						textAlign: 'center',
						display: 'flex',
						maxWidth: 900,
						marginY: 2,
						px: 2,
						marginLeft: 'auto',
						marginRight: 'auto',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<Formik<IRenewalMakePaymentForm>
						initialValues={initialValues}
						validationSchema={checkoutSchema(requiresPayment, isFinance, existingCard, validatePremium)}
						onSubmit={isStripe ? handleStripePaymentSubmit : handleBraintreePaymentSubmit}
					>
						{(formik) => {
							return (
								<Form>
									<FormikLogErrors />
									<PaymentHeader title='Secure Payment Checkout' />

									{requiresPayment && (
										<Box
											sx={(theme) => ({
												background: '#fff',
												p: 2,
												mb: 2,
												borderRadius: 2,
												border: `2px solid ${theme.palette.grey[300]}`,
											})}
										>
											<Typography
												sx={{
													fontSize: '1.3em',
													mt: 0,
													fontWeight: 'bold',
													textAlign: 'center',
													pb: 1,
												}}
											>
												Amount To Pay Today
											</Typography>
											<Typography
												sx={{
													fontWeight: 'bold',
													fontSize: '2.2em',
													color: 'secondary.main',
												}}
											>
												{formatCurrency(renewal.checkout.toPay as number)}
											</Typography>
											<Typography
												sx={{
													fontSize: 14,
													borderTop: 1,
													borderColor: 'grey.300',
													pt: 2,
													mt: 2,
												}}
											>
												You will receive an email as receipt and confirmation of payment.
											</Typography>
										</Box>
									)}

									{isFinance && (
										<Box
											sx={(theme) => ({
												background: '#fff',
												px: 2,
												mb: 2,
												borderRadius: 2,
												border: `2px solid ${theme.palette.grey[300]}`,
											})}
										>
											<AffordabilityLendingQuestions />
										</Box>
									)}

									{isFinance && (
										<Box
											sx={(theme) => ({
												background: '#fff',
												px: 2,
												mb: 2,
												borderRadius: 2,
												border: `2px solid ${theme.palette.grey[300]}`,
											})}
										>
											<DirectDebitDetails
												directDebitDetails={requiredDirectDebitInfo}
												newDirectDebitPaymentDay={newDirectDebitPaymentDay}
											/>
										</Box>
									)}

									<Box
										sx={(theme) => ({
											background: '#fff',
											px: 2,
											borderRadius: 2,
											border: requiresPayment ? `2px solid ${theme.palette.grey[300]}` : 'none',
										})}
									>
										{requiresPayment ? (
											<>
												{hasExistingCards && (
													<Box sx={{ width: '100%', pb: 2, pt: 2 }}>
														<Box sx={{ margin: 2 }} />
														<Box
															sx={{
																margin: 'auto',
																width: '100%',
																textAlign: 'center',
															}}
														>
															{hasExistingCards && (
																<ToggleButtonGroup
																	color='primary'
																	value={useExistingCard}
																	exclusive
																	aria-label='Select Card'
																>
																	<ToggleButton
																		value={false}
																		onClick={() => setUseExistingCard(false)}
																		sx={{ '& svg': { marginRight: '5px' } }}
																		disabled={!hasExistingCards}
																	>
																		<AddCard />
																		New Card
																	</ToggleButton>
																	{hasExistingCards && (
																		<ToggleButton
																			value
																			onClick={() => setUseExistingCard(true)}
																			sx={{
																				'& svg': { marginRight: '5px' },
																			}}
																		>
																			<CreditCard />
																			Existing Card
																		</ToggleButton>
																	)}
																</ToggleButtonGroup>
															)}
														</Box>
													</Box>
												)}

												{isStripe ? (
													<StripePaymentForm submitting={submitting} useExistingCard={useExistingCard} />
												) : (
													<BraintreePaymentForm
														submitting={submitting}
														useExistingCard={useExistingCard}
														amount={renewal.checkout.toPay?.toString() ?? ''}
														RenewalPaymentError={RenewalPaymentError}
													/>
												)}
											</>
										) : (
											<Box sx={{ paddingRight: { xs: 0, md: '20%' } }}>
												<Grid container spacing={{ xs: 0 }}>
													<Grid
														size={{ xs: 6, md: 4 }}
														sx={{
															textAlign: 'right',
														}}
													>
														&nbsp;
													</Grid>
													<Grid size={{ md: 8, xs: 12 }}>
														<LoadingButton
															variant='contained'
															color='secondary'
															fullWidth
															size='large'
															type='submit'
															loading={submitting}
															disabled={!formik.dirty || !formik.isValid || submitting}
														>
															Renew Now
														</LoadingButton>
													</Grid>
												</Grid>
											</Box>
										)}
									</Box>
								</Form>
							);
						}}
					</Formik>
					<ProblemModal
						open={problem}
						title={PAYMENT_PROBLEM_MODAL.header}
						description={PAYMENT_PROBLEM_MODAL.text}
						onClose={() => setProblem(false)}
					/>
				</Box>
			</Fade>
		</Paper>
	);
};

export default MakePayment;
